import React, { useState, useEffect, useRef } from 'react';
import { X, Calculator, CheckCircle, Trash2, AlertTriangle, Save, Eye, Zap, Sun, Tag, Coins, Lightbulb, Upload } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import LembretesViewer from './components/LembretesViewer';
import { Fatura } from '../../types';
import { isPdfEspelho, canSubstitutePdf } from '../../src/lib/faturaUtils';
import { camposZeradosSemInjecao } from '../../src/lib/faturaSemInjecao';
import { conferirSomaComCodigo } from '../../src/lib/contaEquatorial';
import { barrarSeTemBoletoVivo } from '../../src/lib/boletoVivoDaFatura';
import PularCalculoCampo, { ESTADO_PULO_INICIAL, type EstadoPulo } from './components/PularCalculoCampo';
import { gravarCorrecaoFatura, type ResultadoCorrecao } from '../../src/lib/correcaoDadosFatura';

interface FaturaValidationModalProps {
    isOpen: boolean;
    onClose: () => void;
    fatura: Fatura | null;
    onUpdate: () => void;
    onNext?: () => void;
}

const FaturaValidationModal: React.FC<FaturaValidationModalProps> = ({ isOpen, onClose, fatura, onUpdate, onNext }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const backdropMouseDownRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState(false);
    const [skipConfirmation, setSkipConfirmation] = useState(false);
    const [motivoExclusao, setMotivoExclusao] = useState('');
    // Tracks raw string values for numeric fields to allow intermediate typing (e.g. "177,")
    const [rawInputValues, setRawInputValues] = useState<Record<string, string>>({});
    // Motivo do pulo, já conferido contra a injeção da conta (PularCalculoCampo).
    const [estadoPulo, setEstadoPulo] = useState<EstadoPulo>(ESTADO_PULO_INICIAL);
    const [isReplacingPdf, setIsReplacingPdf] = useState(false);
    const [replacingPdfLoading, setReplacingPdfLoading] = useState(false);

    // Conferência dos saldos (solicitação #5951): "Validar Fatura" só avança depois que
    // o analista confirma os quatro saldos. A confirmação vale para esta fatura e para
    // os valores exatos que estavam na tela no clique — se ele corrigir um saldo depois,
    // ou a fila trocar de fatura, ela cai sozinha e precisa ser feita de novo.
    const [saldoConferido, setSaldoConferido] = useState<{ faturaId: string; chave: string; em: string } | null>(null);
    const [destacarSaldos, setDestacarSaldos] = useState(false);
    const saldosRef = useRef<HTMLDivElement>(null);

    // Conferência da conta da Equatorial: quando a soma das parcelas não bate com o código
    // de barras da conta, "Validar Fatura" só avança depois que o analista confere. Mesma
    // regra dos saldos: vale para esta fatura e para os valores da tela no clique.
    const [diferencaConferida, setDiferencaConferida] = useState<string | null>(null);
    const [destacarDiferenca, setDestacarDiferenca] = useState(false);
    const totalRef = useRef<HTMLDivElement>(null);

    // Form State
    // _faturaId carimba de qual fatura este formData foi carregado. A fila de validacao
    // troca a fatura por baixo do modal (handleValidate -> onNext) sem desmontar o
    // componente, e o auto-save tem debounce — sem o carimbo, um save agendado para a
    // fatura anterior dispara depois da troca e grava o formData antigo (uc_codigo junto)
    // na linha nova. Como o carimbo muda junto com o resto do formData, e nao no instante
    // em que a prop troca, ele fecha a janela inteira.
    const [formData, setFormData] = useState({
        _faturaId: '',
        cliente_nome: '',
        uc_codigo: '',
        mes_referencia: '',
        consumo_ativo_kwh: 0,
        energia_injetada_gd1_kwh: 0,
        energia_injetada_gd2_kwh: 0,
        valor_tarifa_distribuidora: 0,
        tarifa_bandeira: 0,
        tarifa_injecao_gd2: 0,
        tarifa_parc_injet_s_desc_gd2: 0,
        consumo_scee_tarifa_c_imposto: 0, // INPUT field
        tarifa_injecao_gd1: 0,
        energia_nao_isenta: 0,
        ilum_publica: 0,
        outros_multas: 0,
        classificacao: '',
        tipo_ligacao: '',
        leitura_anterior: '',
        leitura_atual: '',
        proxima_leitura: '',
        cliente_cpf_cnpj: '',
        end_logradouro: '',
        end_cep: '',
        valor_fatura_distribuidora: 0,
        data_vencimento: '',
        geracao_ciclo: '',
        unidade_geradora_1: '',
        geracao_unidade_geradora_1: 0,
        excedente_recebido_unidade_geradora_1: 0,
        unidade_geradora_2: '',
        geracao_unidade_geradora_2: 0,
        excedente_recebido_unidade_geradora_2: 0,
        unidade_geradora_3: '',
        geracao_unidade_geradora_3: 0,
        excedente_recebido_unidade_geradora_3: 0,
        unidade_geradora_4: '',
        geracao_unidade_geradora_4: 0,
        excedente_recebido_unidade_geradora_4: 0,
        unidade_geradora_5: '',
        geracao_unidade_geradora_5: 0,
        excedente_recebido_unidade_geradora_5: 0,
        injecao_kwh_ug_1: 0,
        injecao_kwh_ug_2: 0,
        injecao_kwh_ug_3: 0,
        injecao_kwh_ug_4: 0,
        injecao_kwh_ug_5: 0,
        saldo_acumulado_kwh: 0,
        saldo_ugs_vigor: 0,
        saldo_ugs_outros: 0,
        credito_recebido: 0,
        numero_medidor: ''
    });

    // Calculated State
    const [calculated, setCalculated] = useState({
        valor_consumo_nao_comp: 0,
        valor_adc_bandeira: 0,
        valor_parc_injet_s_desc: 0,
        valor_restituicao_imposto: 0,
        final_total: 0
    });

    useEffect(() => {
        if (fatura) {
            // O auto-save grava a fatura, o tempo real devolve a mesma fatura como objeto
            // novo e este efeito reescrevia o formulário com o que foi salvo 1s antes —
            // apagando o que o analista digitou nesse meio-tempo (os "últimos números que
            // somem sozinhos"). Durante a edição da MESMA fatura a tela é a fonte da
            // verdade; troca de fatura (fila, onNext) continua recarregando. Mesma guarda
            // do FaturaCalculationModal.
            if (isEditing && String(fatura.id) === String(formData._faturaId)) return;
            setFormData({
                _faturaId: fatura.id,
                cliente_nome: fatura.cliente_nome || '',
                uc_codigo: fatura.uc_codigo || '',
                mes_referencia: fatura.mes_referencia || '',
                consumo_ativo_kwh: Number(fatura.consumo_ativo_kwh || 0),
                energia_injetada_gd1_kwh: Number(fatura.energia_injetada_gd1_kwh || 0),
                energia_injetada_gd2_kwh: Number(fatura.energia_injetada_gd2_kwh || 0),
                valor_tarifa_distribuidora: Number(fatura.valor_tarifa_distribuidora || 0),
                tarifa_bandeira: Number(fatura.tarifa_bandeira || 0),
                tarifa_injecao_gd2: Number(fatura.tarifa_injecao_gd2 || 0),
                tarifa_parc_injet_s_desc_gd2: Number(fatura.tarifa_parc_injet_s_desc_gd2 || 0),
                consumo_scee_tarifa_c_imposto: Number(fatura.consumo_scee_tarifa_c_imposto || 0),
                tarifa_injecao_gd1: Number(fatura.tarifa_injecao_gd1 || 0),
                energia_nao_isenta: Number(fatura.energia_nao_isenta || 0),
                ilum_publica: Number(fatura.ilum_publica || 0),
                outros_multas: Number(fatura.outros_multas || 0),
                classificacao: fatura.classificacao || '',
                tipo_ligacao: fatura.tipo_ligacao || '',
                leitura_anterior: fatura.leitura_anterior ? String(fatura.leitura_anterior) : '',
                leitura_atual: fatura.leitura_atual ? String(fatura.leitura_atual) : '',
                proxima_leitura: fatura.proxima_leitura || '',
                cliente_cpf_cnpj: fatura.cliente_cpf_cnpj || '',
                end_logradouro: fatura.end_logradouro || '',
                end_cep: fatura.end_cep || '',
                valor_fatura_distribuidora: Number(fatura.valor_fatura_distribuidora || 0),
                data_vencimento: fatura.data_vencimento || '',
                geracao_ciclo: fatura.geracao_ciclo || '',
                unidade_geradora_1: fatura.unidade_geradora_1 || '',
                geracao_unidade_geradora_1: Number(fatura.geracao_unidade_geradora_1 || 0),
                excedente_recebido_unidade_geradora_1: Number(fatura.excedente_recebido_unidade_geradora_1 || 0),
                unidade_geradora_2: fatura.unidade_geradora_2 || '',
                geracao_unidade_geradora_2: Number(fatura.geracao_unidade_geradora_2 || 0),
                excedente_recebido_unidade_geradora_2: Number(fatura.excedente_recebido_unidade_geradora_2 || 0),
                unidade_geradora_3: fatura.unidade_geradora_3 || '',
                geracao_unidade_geradora_3: Number(fatura.geracao_unidade_geradora_3 || 0),
                excedente_recebido_unidade_geradora_3: Number(fatura.excedente_recebido_unidade_geradora_3 || 0),
                unidade_geradora_4: fatura.unidade_geradora_4 || '',
                geracao_unidade_geradora_4: Number(fatura.geracao_unidade_geradora_4 || 0),
                excedente_recebido_unidade_geradora_4: Number(fatura.excedente_recebido_unidade_geradora_4 || 0),
                unidade_geradora_5: fatura.unidade_geradora_5 || '',
                geracao_unidade_geradora_5: Number(fatura.geracao_unidade_geradora_5 || 0),
                excedente_recebido_unidade_geradora_5: Number(fatura.excedente_recebido_unidade_geradora_5 || 0),
                injecao_kwh_ug_1: Number(fatura.injecao_kwh_ug_1 || 0),
                injecao_kwh_ug_2: Number(fatura.injecao_kwh_ug_2 || 0),
                injecao_kwh_ug_3: Number(fatura.injecao_kwh_ug_3 || 0),
                injecao_kwh_ug_4: Number(fatura.injecao_kwh_ug_4 || 0),
                injecao_kwh_ug_5: Number(fatura.injecao_kwh_ug_5 || 0),
                saldo_acumulado_kwh: Number(fatura.saldo_acumulado_kwh || 0),
                saldo_ugs_vigor: Number(fatura.saldo_ugs_vigor || 0),
                saldo_ugs_outros: Number(fatura.saldo_ugs_outros || 0),
                credito_recebido: Number(fatura.credito_recebido || 0),
                numero_medidor: fatura.numero_medidor || ''
            });
        }
    }, [fatura]);

    // Real-time calculation
    useEffect(() => {
        const {
            consumo_ativo_kwh,
            energia_injetada_gd1_kwh,
            energia_injetada_gd2_kwh,
            valor_tarifa_distribuidora,
            tarifa_bandeira,
            tarifa_injecao_gd2,
            tarifa_parc_injet_s_desc_gd2,
            consumo_scee_tarifa_c_imposto,
            tarifa_injecao_gd1,
            energia_nao_isenta,
            ilum_publica,
            outros_multas
        } = formData;

        // 1. valor_consumo_nao_comp
        const valor_consumo_nao_comp = (consumo_ativo_kwh - energia_injetada_gd1_kwh - energia_injetada_gd2_kwh) * valor_tarifa_distribuidora;

        // 2. valor_adc_bandeira
        const valor_adc_bandeira = (consumo_ativo_kwh - energia_injetada_gd1_kwh - energia_injetada_gd2_kwh) * tarifa_bandeira;

        // 3. valor_parc_injet_s_desc
        const valor_parc_injet_s_desc = energia_injetada_gd2_kwh * tarifa_parc_injet_s_desc_gd2;

        // 4. valor_restituicao_imposto
        const valor_restituicao_imposto = ((energia_injetada_gd1_kwh + energia_injetada_gd2_kwh) * consumo_scee_tarifa_c_imposto) - ((energia_injetada_gd1_kwh * tarifa_injecao_gd1) + (energia_injetada_gd2_kwh * tarifa_injecao_gd2));

        // Final Total
        const final_total = valor_consumo_nao_comp + valor_adc_bandeira + valor_parc_injet_s_desc + valor_restituicao_imposto + energia_nao_isenta + ilum_publica + outros_multas;

        setCalculated({
            valor_consumo_nao_comp: Math.max(0, valor_consumo_nao_comp), // Ensure non-negative if logic dictates, but formula might allow negative. Keeping precise for now.
            valor_adc_bandeira,
            valor_parc_injet_s_desc,
            valor_restituicao_imposto,
            final_total
        });
    }, [formData]);

    const chaveSaldos = [formData.credito_recebido, formData.saldo_acumulado_kwh, formData.saldo_ugs_vigor, formData.saldo_ugs_outros].join('|');
    const saldosConfirmados = !!saldoConferido
        && saldoConferido.faturaId === String(formData._faturaId)
        && saldoConferido.chave === chaveSaldos;
    // Zero em tudo costuma ser o robô que não leu o quadro de saldos, não saldo zerado.
    // Não bloqueia (pode ser real), só pede para olhar o PDF antes de confirmar.
    const saldosTodosZerados = formData.credito_recebido === 0 && formData.saldo_acumulado_kwh === 0
        && formData.saldo_ugs_vigor === 0 && formData.saldo_ugs_outros === 0;

    const confirmarSaldos = () => {
        setSaldoConferido({ faturaId: String(formData._faturaId), chave: chaveSaldos, em: new Date().toISOString() });
        setDestacarSaldos(false);
    };

    // O total gravado é o do código de barras da conta da Equatorial (src/lib/contaEquatorial.ts).
    // A soma das parcelas passou a ser só a conferência: se não bate, alguma parcela foi lida
    // errada — e uma tarifa errada também muda a parte da Vigor.
    const conferencia = conferirSomaComCodigo(calculated.final_total, fatura?.linha_digitavel_equatorial);
    const chaveDiferenca = `${formData._faturaId}|${conferencia.soma}|${conferencia.codigo}`;
    const diferencaOk = !conferencia.diverge || diferencaConferida === chaveDiferenca;
    const podeValidar = saldosConfirmados && diferencaOk;

    const confirmarDiferenca = () => {
        setDiferencaConferida(chaveDiferenca);
        setDestacarDiferenca(false);
    };

    const handleInputChange =(e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? (parseFloat(value) || 0) : value
        }));
    };

    // Handles numeric text inputs that accept both comma and period as decimal separators.
    const handleNumericInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        // Strip anything that isn't a digit, period, comma, or leading minus
        const stripped = value.replace(/[^0-9.,\-]/g, '');
        
        setRawInputValues(prev => ({ ...prev, [name]: stripped }));
        
        let parseableStr = stripped;
        // Se tem vírgula, assume que o usuário está digitando no padrão brasileiro (ex: 125.158,69)
        if (stripped.includes(',')) {
            // Remove todos os pontos (separadores de milhar) e converte a primeira vírgula para ponto (decimal)
            parseableStr = stripped.replace(/\./g, '').replace(',', '.');
            // Remove vírgulas adicionais caso o usuário digite sem querer
            const firstDot = parseableStr.indexOf('.');
            if (firstDot >= 0) {
                parseableStr = parseableStr.slice(0, firstDot + 1) + parseableStr.slice(firstDot + 1).replace(/,/g, '');
            }
        } else {
            // Se não tem vírgula, verifica a quantidade de pontos
            const parts = stripped.split('.');
            if (parts.length > 2) {
                // Mais de um ponto (ex: 125.158.000) -> trata como milhar e remove todos os pontos
                parseableStr = stripped.replace(/\./g, '');
            } else if (parts.length === 2 && parts[1].length === 3) {
                // Se tem um ponto e exatamente 3 casas depois, assume que é separador de milhar (ex: 1.600 -> 1600)
                // Se o usuário quiser 1.600 com precisão decimal, ele deve digitar 1,600.
                parseableStr = stripped.replace(/\./g, '');
            } else {
                // Um ou zero pontos (ex: 125.15 ou 125) -> mantém normal
                parseableStr = stripped;
            }
        }
        
        // Parse immediately so calculations stay live
        const parsed = parseFloat(parseableStr) || 0;
        setFormData(prev => ({ ...prev, [name]: parsed }));
    };

    const handleNumericBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        // Clear raw value on blur so the input shows the clean number
        setRawInputValues(prev => { const n = { ...prev }; delete n[name]; return n; });
    };

    // Returns the display value for a numeric field: raw string while typing, number otherwise
    const numVal = (name: string, fallback: number | string | null | undefined): string | number => {
        if (name in rawInputValues) return rawInputValues[name];
        
        const num = typeof fallback === 'string' ? parseFloat(fallback) : fallback;
        if (typeof num !== 'number' || isNaN(num)) return fallback || '';
        
        return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Último erro de gravação já avisado: o salvamento automático roda a cada pausa da
    // digitação e repetiria o mesmo aviso a cada segundo.
    const ultimoAvisoRef = useRef('');
    const avisarErroGravacao = (r: ResultadoCorrecao | null, sempre = false) => {
        const texto = [r?.erro && `Os dados não foram salvos: ${r.erro}`, r?.erroUc && `O número da UC não foi salvo: ${r.erroUc}`]
            .filter(Boolean).join(' ');
        if (!texto) { ultimoAvisoRef.current = ''; return; }
        if (!sempre && texto === ultimoAvisoRef.current) return;
        ultimoAvisoRef.current = texto;
        addToast(texto, 'error', 15000);
    };

    // `comUc`: o número da UC só vai ao concluir a edição, ao fechar e ao validar (ver
    // src/lib/correcaoDadosFatura.ts). O erro do banco agora volta para quem chamou.
    const saveChanges = async (opcoes?: { comUc?: boolean }): Promise<ResultadoCorrecao | null> => {
        if (!fatura) return null;
        // Só grava se o formData carregado ainda for o desta fatura (ver _faturaId).
        if (formData._faturaId !== fatura.id) return null;
        const faturaId = fatura.id;
        try {
            return await gravarCorrecaoFatura(supabase, faturaId, {
                cliente_nome: formData.cliente_nome,
                mes_referencia: formData.mes_referencia,
                consumo_ativo_kwh: formData.consumo_ativo_kwh,
                energia_injetada_gd1_kwh: formData.energia_injetada_gd1_kwh,
                energia_injetada_gd2_kwh: formData.energia_injetada_gd2_kwh,
                valor_tarifa_distribuidora: formData.valor_tarifa_distribuidora,
                tarifa_bandeira: formData.tarifa_bandeira,
                tarifa_injecao_gd2: formData.tarifa_injecao_gd2,
                tarifa_parc_injet_s_desc_gd2: formData.tarifa_parc_injet_s_desc_gd2,
                consumo_scee_tarifa_c_imposto: formData.consumo_scee_tarifa_c_imposto,
                tarifa_injecao_gd1: formData.tarifa_injecao_gd1,
                energia_nao_isenta: formData.energia_nao_isenta,
                ilum_publica: formData.ilum_publica,
                outros_multas: formData.outros_multas,
                classificacao: formData.classificacao,
                tipo_ligacao: formData.tipo_ligacao,
                leitura_anterior: formData.leitura_anterior,
                leitura_atual: formData.leitura_atual,
                proxima_leitura: formData.proxima_leitura,
                cliente_cpf_cnpj: formData.cliente_cpf_cnpj,
                end_logradouro: formData.end_logradouro,
                end_cep: formData.end_cep,
                valor_fatura_distribuidora: formData.valor_fatura_distribuidora,
                data_vencimento: formData.data_vencimento,
                geracao_ciclo: formData.geracao_ciclo,
                unidade_geradora_1: formData.unidade_geradora_1,
                geracao_unidade_geradora_1: formData.geracao_unidade_geradora_1,
                excedente_recebido_unidade_geradora_1: formData.excedente_recebido_unidade_geradora_1,
                unidade_geradora_2: formData.unidade_geradora_2,
                geracao_unidade_geradora_2: formData.geracao_unidade_geradora_2,
                excedente_recebido_unidade_geradora_2: formData.excedente_recebido_unidade_geradora_2,
                unidade_geradora_3: formData.unidade_geradora_3,
                geracao_unidade_geradora_3: formData.geracao_unidade_geradora_3,
                excedente_recebido_unidade_geradora_3: formData.excedente_recebido_unidade_geradora_3,
                unidade_geradora_4: formData.unidade_geradora_4,
                geracao_unidade_geradora_4: formData.geracao_unidade_geradora_4,
                excedente_recebido_unidade_geradora_4: formData.excedente_recebido_unidade_geradora_4,
                unidade_geradora_5: formData.unidade_geradora_5,
                geracao_unidade_geradora_5: formData.geracao_unidade_geradora_5,
                excedente_recebido_unidade_geradora_5: formData.excedente_recebido_unidade_geradora_5,
                injecao_kwh_ug_1: formData.injecao_kwh_ug_1,
                injecao_kwh_ug_2: formData.injecao_kwh_ug_2,
                injecao_kwh_ug_3: formData.injecao_kwh_ug_3,
                injecao_kwh_ug_4: formData.injecao_kwh_ug_4,
                injecao_kwh_ug_5: formData.injecao_kwh_ug_5,
                saldo_acumulado_kwh: formData.saldo_acumulado_kwh,
                saldo_ugs_vigor: formData.saldo_ugs_vigor,
                saldo_ugs_outros: formData.saldo_ugs_outros,
                credito_recebido: formData.credito_recebido,
                numero_medidor: formData.numero_medidor || null,

                // Calculated values
                valor_consumo_nao_comp: calculated.valor_consumo_nao_comp,
                valor_adc_bandeira: calculated.valor_adc_bandeira,
                valor_parc_injet_s_desc: calculated.valor_parc_injet_s_desc,
                valor_restituicao_imposto: calculated.valor_restituicao_imposto
            }, opcoes?.comUc ? { digitada: formData.uc_codigo, atual: fatura.uc_codigo } : undefined);
        } catch (error: any) {
            console.error("Error auto-saving:", error);
            return { erro: error?.message || 'Falha de conexão ao salvar.', erroUc: null };
        }
    };

    // Debounced Save
    // `fatura?.id`, e não o objeto: cada gravação volta pelo tempo real como objeto novo
    // da mesma fatura, e com o objeto aqui ela agendava outra gravação — um ciclo sem
    // fim, a cada ~2s, enquanto o modal ficasse em edição (fatura 13854, 13/10/2026).
    useEffect(() => {
        if (!isEditing || !fatura) return;

        const timer = setTimeout(() => {
            saveChanges().then(r => avisarErroGravacao(r));
        }, 1000); // 1s debounce

        return () => clearTimeout(timer);
    }, [formData, calculated, isEditing, fatura?.id]);

    // Fechar o modal encerra a edição: sem isto, reabrir a mesma fatura voltava em modo
    // de edição e a guarda do efeito que carrega a fatura mantinha o formulário velho.
    useEffect(() => {
        if (!isOpen) setIsEditing(false);
    }, [isOpen]);

    // As telas de confirmação (pular cálculo, excluir, trocar o PDF) são da fatura em que
    // foram abertas. O modal fica montado entre uma fatura e outra — o FinanceiroLayout só
    // troca `isOpen` e a fatura — e sem isto a seguinte abria direto na ação da anterior:
    // depois de pular uma sem injeção, a próxima já vinha em "Não precisa calcular?";
    // depois de excluir, em "Excluir Fatura?" com o motivo da anterior escrito.
    useEffect(() => {
        setSkipConfirmation(false);
        setEstadoPulo(ESTADO_PULO_INICIAL);
        setDeleteConfirmation(false);
        setMotivoExclusao('');
        setIsReplacingPdf(false);
        setRawInputValues({});
        setDestacarSaldos(false);
        setDestacarDiferenca(false);
    }, [isOpen, fatura?.id]);

    // Fechar (X ou clique fora) logo depois de digitar desarmava o auto-save pendente e
    // o último segundo de digitação não era gravado.
    const fechar = () => {
        if (isEditing) saveChanges({ comUc: true }).then(r => avisarErroGravacao(r, true));
        onClose();
    };

    // Recusa do banco mantém a edição aberta com o aviso: antes a tela saía da edição,
    // recarregava a fatura e o número digitado sumia sem explicação.
    const handleStopEditing = async () => {
        const r = await saveChanges({ comUc: true });
        if (r?.erro || r?.erroUc) {
            avisarErroGravacao(r, true);
            return;
        }
        ultimoAvisoRef.current = '';
        setIsEditing(false);
        onUpdate();
    };


    const handleValidate = async () => {
        if (!fatura) return;
        if (!saldosConfirmados || !saldoConferido) {
            setDestacarSaldos(true);
            saldosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            addToast('Confira os saldos com o PDF da fatura e clique em "Confirmar saldos" antes de validar.', 'warning');
            return;
        }
        if (!diferencaOk) {
            setDestacarDiferenca(true);
            totalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            addToast('A soma das parcelas não bate com a conta da Equatorial. Confira as tarifas com o PDF e clique em "Conferi as tarifas" antes de validar.', 'warning');
            return;
        }
        setLoading(true);
        try {
            // Antes de gravar qualquer coisa: a tela pode estar velha. Em 16/10/2026 a
            // fatura 14192 foi validada de novo 4 minutos depois de o boleto sair e voltou
            // da fila de envio para o cálculo (ver src/lib/boletoVivoDaFatura.ts).
            await barrarSeTemBoletoVivo(fatura.id, 'validar');

            // Save latest values first to be sure
            const gravacao = await saveChanges({ comUc: true });
            if (gravacao?.erro) throw new Error(gravacao.erro);
            if (gravacao?.erroUc) throw new Error(`o número da UC não foi salvo: ${gravacao.erroUc}`);
            // O erro era ignorado aqui: a fila avançava para a próxima fatura mesmo com o
            // UPDATE recusado, e esta ficava em "Fatura disponível" sem ninguém perceber.
            const { error: erroUpdate } = await supabase.from('faturas').update({
                status_fatura: 'Fatura validada',
                // Total da conta da Equatorial: o valor do código de barras dela. A soma das
                // parcelas só vale quando a conta não tem código — ela não fecha em tarifa
                // social, e foi assim que o boleto único 6938 (fatura 14432) cobrou R$ 59,49
                // por uma conta de R$ 46,94.
                valor_fatura_distribuidora: conferencia.totalParaGravar,
                // Ensure calculated values are saved
                valor_consumo_nao_comp: calculated.valor_consumo_nao_comp,
                valor_adc_bandeira: calculated.valor_adc_bandeira,
                valor_parc_injet_s_desc: calculated.valor_parc_injet_s_desc,
                valor_restituicao_imposto: calculated.valor_restituicao_imposto,
                vencimento_boleto: fatura.data_vencimento,
                saldo_conferido_em: saldoConferido.em,
                saldo_conferido_por_nome: user?.user_metadata?.full_name || user?.email || 'Usuário Desconhecido'
            }).eq('id', fatura.id);
            if (erroUpdate) throw erroUpdate;

            await logAction(user?.id || 'SYSTEM', 'VALIDATE_INVOICE', {
                fatura_id: fatura.id,
                saldos_conferidos: {
                    credito_recebido: formData.credito_recebido,
                    saldo_mes: formData.saldo_acumulado_kwh,
                    saldo_ugs_vigor: formData.saldo_ugs_vigor,
                    saldo_ugs_outros: formData.saldo_ugs_outros
                },
                conta_equatorial: {
                    gravado: conferencia.totalParaGravar,
                    codigo_barras: conferencia.codigo,
                    soma_parcelas: conferencia.soma,
                    diferenca_conferida: conferencia.diverge
                }
            });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'validar_fatura', origem: 'acao' });

            // Encerra a edicao antes de avancar: sem isto o modo de edicao (e o auto-save
            // armado junto com ele) atravessa a troca de fatura feita pelo onNext.
            setIsEditing(false);

            onUpdate(); // Refresh parent
            if (onNext) {
                onNext();
            } else {
                onClose();
            }
        } catch (error: any) {
            console.error(error);
            addToast(error?.message ? `Erro ao validar fatura: ${error.message}` : 'Erro ao validar fatura.', 'error', 12000);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!fatura) return;
        if (!motivoExclusao.trim()) {
            addToast('Por favor, informe o motivo da exclusão.', 'warning');
            return;
        }
        setLoading(true);
        try {
            await supabase.from('faturas').update({
                status_fatura: 'Excluído',
                excluido_por_nome: user?.user_metadata?.full_name || user?.email || 'Usuário Desconhecido',
                excluido_em: new Date().toISOString(),
                motivo_exclusao: motivoExclusao.trim()
            }).eq('id', fatura.id);

            await logAction(user?.id || 'SYSTEM', 'DELETE_INVOICE', {
                fatura_id: fatura.id,
                motivo: motivoExclusao.trim()
            });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'excluir_fatura', origem: 'acao' });
            onUpdate();
            onClose();
        } catch (error) {
            console.error(error);
            addToast('Erro ao excluir fatura.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSkipCalculation = async () => {
        if (!fatura) return;
        const camposPulo = estadoPulo.campos;
        if (!camposPulo) {
            addToast(estadoPulo.erro || 'Selecione o motivo.', 'warning');
            return;
        }
        setLoading(true);
        try {
            // O erro era ignorado aqui: a tela dizia "Ação registrada" mesmo quando o
            // UPDATE falhava. Com a coluna de código com CHECK, isso não pode passar calado.
            const { error: erroUpdate } = await supabase.from('faturas').update({
                status_fatura: 'Com boleto emitido / A enviar',
                pular_calculo_por_nome: user?.user_metadata?.full_name || user?.email || 'Usuário Desconhecido',
                pular_calculo_em: new Date().toISOString(),
                ...camposPulo,
                // Sem injeção não há cobrança da Vigor: zera o valor do boleto e todo o
                // detalhamento *_boleto. Sem isto, o valor pré-preenchido na importação
                // (ou herdado de uma duplicata da mesma UC) permanece e o card mostra um
                // boleto "Vencido" fantasma de milhares de reais. Fonte única do payload
                // em src/lib/faturaSemInjecao.ts — reaproveitada também por
                // FaturaCalculationModal e PendentesCalculoModal.
                ...camposZeradosSemInjecao(fatura)
            }).eq('id', fatura.id);
            if (erroUpdate) throw erroUpdate;

            await logAction(user?.id || 'SYSTEM', 'SKIP_CALCULATION', {
                fatura_id: fatura.id,
                motivo: camposPulo.motivo_pular_calculo,
                motivo_codigo: camposPulo.motivo_pular_calculo_codigo,
                situacao_injecao: camposPulo.pular_calculo_detalhe.situacao
            });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'pular_calculo', origem: 'acao' });

            onUpdate();
            onClose();
            // Sem boleto vivo não há cobrança da Vigor: o texto antigo ("enviada para cobrança") contradizia
            // o que a pessoa via depois, a fatura fora de "Vencidos" (#6246).
            addToast(fatura.num_cobranca_boleto
                ? 'Ação registrada. Esta fatura ainda tem boleto emitido e segue em cobrança.'
                : 'Ação registrada. A fatura fica sem cobrança da Vigor neste ciclo e deixa de contar como vencida.', 'success');
        } catch (error) {
            console.error(error);
            // A recusa do banco (trg_validar_pulo_calculo) já vem em português e diz o que falta.
            addToast(`Erro ao registrar ação: ${(error as any)?.message || error}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleReplacePdf = async (file: File) => {
        if (!fatura) return;
        setReplacingPdfLoading(true);
        try {
            const { data: webhookData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'pdf_definitivo_webhook_url')
                .single();

            const webhookUrl = webhookData?.value;

            if (!webhookUrl) {
                throw new Error('Webhook de substituição de PDF não configurado em APIs e Webhooks.');
            }

            const safeFilename = `${(fatura.mes_referencia || 'fatura').replace('/', '_')}-${Date.now()}.pdf`;

            const formData = new FormData();
            formData.append('fatura_padrao', file, safeFilename);
            formData.append('fatura_id', fatura.id);
            formData.append('uc_codigo', fatura.uc_codigo || '');
            formData.append('conta_mes', fatura.mes_referencia || '');
            formData.append('file_name', safeFilename);

            const uploadResponse = await fetch(webhookUrl, {
                method: 'POST',
                body: formData,
            });

            if (!uploadResponse.ok) {
                throw new Error(`Erro no webhook: ${uploadResponse.status}`);
            }

            addToast('PDF definitivo substituído com sucesso!', 'success');
            setIsReplacingPdf(false);
            onUpdate();
            onClose();
        } catch (error: any) {
            addToast(error.message || 'Erro ao substituir PDF.', 'error');
        } finally {
            setReplacingPdfLoading(false);
        }
    };

    if (!isOpen || !fatura) return null;

    return (
        createPortal(
            <div
                className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4"
                style={{ zIndex: 9999 }}
                onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
                onClick={() => { if (backdropMouseDownRef.current) fechar(); backdropMouseDownRef.current = false; }}
            >
                {deleteConfirmation ? (
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertTriangle size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Excluir Fatura?</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6">
                            Tem certeza que deseja excluir esta fatura? O registro será arquivado no sistema.
                        </p>
                        <div className="mb-6 text-left px-2">
                            <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Motivo da Exclusão <span className="text-red-500">*</span></label>
                            <textarea
                                value={motivoExclusao}
                                onChange={(e) => setMotivoExclusao(e.target.value)}
                                placeholder="Descreva o motivo da solicitação..."
                                className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-3 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all min-h-[100px] resize-none"
                                required
                            />
                        </div>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => {
                                    setDeleteConfirmation(false);
                                    setMotivoExclusao('');
                                }}
                                className="flex-1 px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={loading || !motivoExclusao.trim()}
                                className="flex-1 px-6 py-2.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-500/20"
                            >
                                {loading ? 'Excluindo...' : 'Confirmar Exclusão'}
                            </button>
                        </div>
                    </div>
                ) : skipConfirmation ? (
                    // Altura presa à tela e só o meio rola: com usinas a conferir, o conteúdo
                    // passava da tela e escondia o título e os botões.
                    <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in border dark:border-gray-700">
                        <div className="shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 shrink-0 bg-orange-100 dark:bg-orange-900/30 text-orange-500 rounded-full flex items-center justify-center">
                                    <Calculator size={20} />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Não precisa calcular?</h3>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {fatura.num_cobranca_boleto
                                    ? 'Ao prosseguir, as variáveis de cálculo serão zeradas. Esta fatura ainda tem boleto emitido: o boleto não é cancelado por aqui e o valor dele é mantido.'
                                    : 'Ao prosseguir, as variáveis de cálculo e o valor do boleto serão zerados e a fatura fica sem cobrança da Vigor neste ciclo: ela sai de "Vencidos" e passa a aparecer como "Sem injeção" ou "Sem cobrança".'}
                            </p>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                            <PularCalculoCampo key={String(fatura.id)} faturaId={fatura?.id ?? null} onChange={setEstadoPulo} />
                        </div>
                        <div className="shrink-0 flex gap-3 justify-center px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60">
                            <button
                                onClick={() => {
                                    setSkipConfirmation(false);
                                    setEstadoPulo(ESTADO_PULO_INICIAL);
                                }}
                                className="flex-1 px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Voltar
                            </button>
                            <button
                                onClick={handleSkipCalculation}
                                disabled={loading || !estadoPulo.campos}
                                className="flex-1 px-6 py-2.5 rounded-xl bg-secondary text-white font-bold hover:bg-secondary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-secondary/20"
                            >
                                {loading ? 'Processando...' : 'Confirmar e pular cálculo'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800/95 rounded-3xl shadow-2xl w-[95%] max-h-[90vh] flex flex-col border dark:border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="p-8 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/40 shrink-0 z-10 backdrop-blur-md">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                                    <CheckCircle className="text-primary" size={28} />
                                    Confira se essas informações estão corretas:
                                </h2>
                            </div>
                            <div className="flex items-center gap-2">
                                {fatura.pdf_fatura && (
                                    <button
                                        onClick={() => window.open(fatura.pdf_fatura, '_blank')}
                                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-secondary hover:bg-secondary/80 rounded-lg transition-colors mr-2"
                                    >
                                        <Eye size={16} />
                                        Ver PDF da fatura
                                    </button>
                                )}
                                {canSubstitutePdf(fatura) && (
                                    <div className="flex items-center gap-2 mr-2">
                                        {isPdfEspelho(fatura) && (
                                            <span className="text-xs font-bold text-red-500 flex items-center gap-1">
                                                <AlertTriangle size={14} /> Espelho
                                            </span>
                                        )}
                                        {!isReplacingPdf ? (
                                            <button
                                                onClick={() => setIsReplacingPdf(true)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white rounded-lg transition-colors ${isPdfEspelho(fatura) ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'}`}
                                            >
                                                <Upload size={14} />
                                                Substituir PDF
                                            </button>
                                        ) : (
                                            <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-primary hover:bg-primary/80 rounded-lg cursor-pointer transition-colors">
                                                <Upload size={14} />
                                                {replacingPdfLoading ? 'Enviando...' : 'Selecionar PDF...'}
                                                <input
                                                    type="file"
                                                    accept="application/pdf"
                                                    className="hidden"
                                                    disabled={replacingPdfLoading}
                                                    onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) handleReplacePdf(file);
                                                    }}
                                                />
                                            </label>
                                        )}
                                    </div>
                                )}
                                <button onClick={fechar} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                                    <X size={24} className="text-gray-400" />
                                </button>
                            </div>
                        </div>

                        {/* Lembretes */}
                        <div className="px-8 mt-6 shrink-0">
                            <LembretesViewer uc_codigo={fatura.uc_codigo} mes_referencia={fatura.mes_referencia} fatura_id={fatura.id} />
                        </div>

                        {/* Cancellation Alert */}
                        {fatura.cancelado_por_nome && (
                            <div className="mx-8 mt-6 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 p-4 rounded-r-lg shadow-sm shrink-0">
                                <div className="flex gap-3">
                                    <AlertTriangle className="text-red-500 shrink-0" size={20} />
                                    <div>
                                        <h3 className="font-bold text-red-800 dark:text-red-400 text-sm">Boleto Cancelado Anteriormente</h3>
                                        <p className="text-red-700 dark:text-red-300 text-sm mt-1">
                                            Cancelamento solicitado por <strong>{fatura.cancelado_por_nome}</strong>
                                            {fatura.cancelado_em && (() => {
                                                try {
                                                    const d = new Date(fatura.cancelado_em);
                                                    if (isNaN(d.getTime())) return ` em ${fatura.cancelado_em}`;
                                                    return ` em ${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
                                                } catch (e) {
                                                    return ` em ${fatura.cancelado_em}`;
                                                }
                                            })()}.
                                        </p>
                                        {fatura.motivo_cancelamento_boleto && (
                                            <p className="text-red-700 dark:text-red-300 text-sm mt-1">
                                                <span className="font-semibold">Motivo:</span> {fatura.motivo_cancelamento_boleto}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Body Container */}
                        <div className="flex-1 flex overflow-hidden">
                            {/* Fixed Results Sidebar */}
                            <div className="w-80 shrink-0 bg-white dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700 p-8 overflow-y-auto">
                                <h4 className="text-xs font-black uppercase tracking-widest text-[#5F6C37] mb-6 border-b border-[#5F6C37]/20 pb-2">Resultados Calculados</h4>

                                <div className="space-y-4">
                                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 space-y-1">
                                        <label className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Valor Consumo Não Comp.</label>
                                        <div className="text-xl font-black text-gray-900 dark:text-white">
                                            R$ {calculated.valor_consumo_nao_comp.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 space-y-1">
                                        <label className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Valor Adc. Bandeira</label>
                                        <div className="text-xl font-black text-gray-900 dark:text-white">
                                            R$ {calculated.valor_adc_bandeira.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 space-y-1">
                                        <label className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Valor Parc. Injet s/ Desc</label>
                                        <div className="text-xl font-black text-gray-900 dark:text-white">
                                            R$ {calculated.valor_parc_injet_s_desc.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 space-y-1">
                                        <label className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Valor Restituição Imposto</label>
                                        <div className="text-xl font-black text-gray-900 dark:text-white">
                                            R$ {calculated.valor_restituicao_imposto.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Scrollable Input Area */}
                            <div className="flex-1 p-8 bg-[#fdfcfd] dark:bg-gray-900/20 overflow-y-auto">
                                <h4 className="text-xs font-black uppercase tracking-widest text-[#5F6C37] mb-6 border-b border-[#5F6C37]/20 pb-2">DADOS DA FATURA</h4>

                                {/* Alerta Crítico: Reaviso de Vencimento */}
                                {fatura?.tem_reaviso_vencimento && (
                                    <div className="mb-6 rounded-xl border-2 border-red-500 overflow-hidden shadow-lg">
                                        <div className="bg-red-600 px-4 py-2 flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003ZM12 8.25a.75.75 0 0 1 .75.75v3.75a.75.75 0 0 1-1.5 0V9a.75.75 0 0 1 .75-.75Zm0 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" /></svg>
                                            <span className="text-white font-black uppercase text-sm tracking-wider">Reaviso de Vencimento — Risco de Corte de Energia</span>
                                        </div>
                                        <div className="bg-red-50 dark:bg-red-950/40 px-4 py-3">
                                            <p className="text-red-800 dark:text-red-200 text-xs font-medium leading-relaxed whitespace-pre-wrap">
                                                {fatura.reaviso_vencimento_texto || 'Esta fatura contém aviso de suspensão do fornecimento por débitos em aberto.'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Visualização Estilo Fatura de Energia */}
                                <div className="mb-8 p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                                    {/* Seção 1: Classificação e Leituras */}
                                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                                        <div className="flex-1 flex flex-col md:flex-row border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
                                            <div className="flex-[3] p-2 border-r border-gray-200 dark:border-gray-600">
                                                <label className="text-[11px] text-gray-500 uppercase font-bold">Classificação</label>
                                                <input className="w-full text-xs font-medium bg-transparent focus:outline-none dark:text-white" value={formData.classificacao} onChange={handleInputChange} name="classificacao" disabled={!isEditing} placeholder="Classificação" />
                                            </div>
                                            <div className="flex-[2] p-2">
                                                <label className="text-[11px] text-gray-500 uppercase font-bold">Tipo de fornecimento</label>
                                                <input className="w-full text-xs font-medium bg-transparent focus:outline-none dark:text-white" value={formData.tipo_ligacao} onChange={handleInputChange} name="tipo_ligacao" disabled={!isEditing} placeholder="Tipo de fornecimento" />
                                            </div>
                                        </div>
                                        <div className="flex-[1] flex border border-gray-200 dark:border-gray-600 rounded-md overflow-hidden">
                                            <div className="w-36 p-2 border-r border-gray-200 dark:border-gray-600 bg-blue-500 text-center flex items-center justify-center font-bold text-white text-xs">
                                                Data das Leituras
                                            </div>
                                            <div className="flex-1 p-2 border-r border-gray-200 dark:border-gray-600 text-center">
                                                <label className="text-[11px] text-gray-500 uppercase font-bold block">Leitura Anterior</label>
                                                <input type="date" className="w-full text-xs font-bold bg-transparent focus:outline-none text-center dark:text-white" value={formData.leitura_anterior} onChange={handleInputChange} name="leitura_anterior" disabled={!isEditing} />
                                            </div>
                                            <div className="flex-1 p-2 border-r border-gray-200 dark:border-gray-600 text-center">
                                                <label className="text-[11px] text-gray-500 uppercase font-bold block">Leitura Atual</label>
                                                <input type="date" className="w-full text-xs font-bold bg-transparent focus:outline-none text-center dark:text-white" value={formData.leitura_atual} onChange={handleInputChange} name="leitura_atual" disabled={!isEditing} />
                                            </div>
                                            <div className="flex-1 p-2 text-center">
                                                <label className="text-[11px] text-gray-500 uppercase font-bold block">Próxima Leitura</label>
                                                <input type="date" className="w-full text-xs font-bold bg-transparent focus:outline-none text-center dark:text-white" value={formData.proxima_leitura} onChange={handleInputChange} name="proxima_leitura" disabled={!isEditing} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Seção 2: Dados do Cliente e UC */}
                                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                                        <div className="flex-[4] border border-gray-200 dark:border-gray-600 rounded-md p-3">
                                            <div className="mb-1 flex items-center">
                                                <label className="text-xs text-gray-500 uppercase font-bold w-36">Nome na fatura:</label>
                                                <input className="flex-1 text-xs font-bold bg-transparent focus:outline-none dark:text-white" value={formData.cliente_nome} onChange={handleInputChange} name="cliente_nome" disabled={!isEditing} />
                                            </div>
                                            <div className="mb-1 flex items-center">
                                                <label className="text-xs text-gray-500 uppercase font-bold w-36">CPF/CNPJ:</label>
                                                <input className="flex-1 text-xs bg-transparent focus:outline-none dark:text-white" value={formData.cliente_cpf_cnpj} onChange={handleInputChange} name="cliente_cpf_cnpj" disabled={!isEditing} />
                                            </div>
                                            <div className="mb-1 flex items-center">
                                                <label className="text-xs text-gray-500 uppercase font-bold w-36">Endereço:</label>
                                                <input className="flex-1 text-xs bg-transparent focus:outline-none dark:text-white" value={formData.end_logradouro} onChange={handleInputChange} name="end_logradouro" disabled={!isEditing} />
                                            </div>
                                            <div className="flex items-center">
                                                <label className="text-xs text-gray-500 uppercase font-bold w-36">CEP:</label>
                                                <input className="flex-1 text-xs bg-transparent focus:outline-none dark:text-white" value={formData.end_cep} onChange={handleInputChange} name="end_cep" disabled={!isEditing} />
                                            </div>
                                        </div>

                                        <div className="flex-[3] flex flex-col gap-2">
                                            <div className="bg-[#449138] rounded-md p-2 text-center text-white flex flex-col justify-center flex-1">
                                                <span className="text-xs font-bold uppercase tracking-wider mb-1">Número da UC</span>
                                                <input className="w-full text-lg font-black bg-transparent focus:outline-none text-center text-white placeholder-white/70" value={formData.uc_codigo} onChange={handleInputChange} name="uc_codigo" disabled={!isEditing} />
                                            </div>

                                            <div className="flex border border-blue-300 dark:border-blue-700 rounded-md overflow-hidden">
                                                <div className="bg-blue-500 text-white px-3 flex items-center justify-center text-xs font-bold uppercase whitespace-nowrap">
                                                    Medidor
                                                </div>
                                                <input
                                                    className="flex-1 text-sm font-bold bg-white dark:bg-gray-800 focus:outline-none text-center py-1.5 text-gray-800 dark:text-white placeholder-gray-400"
                                                    value={formData.numero_medidor}
                                                    onChange={handleInputChange}
                                                    name="numero_medidor"
                                                    disabled={!isEditing}
                                                    placeholder="—"
                                                />
                                            </div>

                                            <div className="flex border border-[#449138] rounded-md overflow-hidden">
                                                <div className="flex-1 text-center">
                                                    <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-bold py-1 uppercase border-b border-blue-200">Mês Ref.</div>
                                                    <input className="w-full text-[14px] font-bold bg-transparent focus:outline-none text-center py-1.5 dark:text-white" value={formData.mes_referencia} onChange={handleInputChange} name="mes_referencia" disabled={!isEditing} />
                                                </div>
                                                <div className="flex-1 text-center border-l border-r border-[#449138]">
                                                    <div className="bg-[#449138] text-white text-xs font-bold py-1 uppercase">Total a pagar</div>
                                                    <div className="flex justify-center items-center py-1.5 bg-green-50 dark:bg-green-900/20">
                                                        <span className="text-[14px] font-bold mr-1 text-gray-900 dark:text-white">R$</span>
                                                        <input type="number" step="0.01" className="w-20 text-[14px] font-bold bg-transparent focus:outline-none text-gray-900 dark:text-white" value={formData.valor_fatura_distribuidora} onChange={handleInputChange} name="valor_fatura_distribuidora" disabled={!isEditing} />
                                                    </div>
                                                </div>
                                                <div className="flex-1 text-center">
                                                    <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-bold py-1 uppercase border-b border-blue-200">Vencimento</div>
                                                    <input type="date" className="w-full text-[14px] font-bold bg-transparent focus:outline-none text-center py-1.5 dark:text-white" value={formData.data_vencimento} onChange={handleInputChange} name="data_vencimento" disabled={!isEditing} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Seção 3: Mensagens / Geração */}
                                    <div className="border border-[#449138] rounded-md overflow-hidden">
                                        <div className="bg-[#449138] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider">
                                            Mensagens Importantes
                                        </div>
                                        <div className="p-3 text-xs text-gray-700 dark:text-gray-300">
                                            <div className="flex items-center mb-3">
                                                <span className="font-bold mr-2">Geração ciclo:</span>
                                                <input type="text" className="w-32 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1 text-right" value={formData.geracao_ciclo} onChange={handleInputChange} name="geracao_ciclo" disabled={!isEditing} />
                                            </div>

                                            <div className="flex flex-col md:flex-row gap-6 text-xs">
                                                <div className="flex-[2] space-y-1.5">
                                                    {[1, 2, 3, 4, 5].map((i) => (
                                                        <div key={i} className="flex gap-2 items-center">
                                                            <span className="font-bold w-10">UG {i}:</span>
                                                            <input className="w-32 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1" value={formData[`unidade_geradora_${i}` as keyof typeof formData] as string} onChange={handleInputChange} name={`unidade_geradora_${i}`} disabled={!isEditing} />

                                                            <span className="font-bold w-28 text-right">Geração UG {i}:</span>
                                                            <input type="text" inputMode="decimal" className="w-32 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1 text-center" value={numVal(`geracao_unidade_geradora_${i}`, formData[`geracao_unidade_geradora_${i}` as keyof typeof formData] as number)} onChange={handleNumericInput} onBlur={handleNumericBlur} name={`geracao_unidade_geradora_${i}`} disabled={!isEditing} />

                                                            <span className="font-bold w-32 text-right">Excedente rec. UG{i}:</span>
                                                            <input type="text" inputMode="decimal" className="w-32 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1 text-center" value={numVal(`excedente_recebido_unidade_geradora_${i}`, formData[`excedente_recebido_unidade_geradora_${i}` as keyof typeof formData] as number)} onChange={handleNumericInput} onBlur={handleNumericBlur} name={`excedente_recebido_unidade_geradora_${i}`} disabled={!isEditing} />

                                                            <span className="font-bold w-24 text-right">Injeção UG {i}:</span>
                                                            <input type="text" inputMode="decimal" className="w-32 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1 text-center" value={numVal(`injecao_kwh_ug_${i}`, formData[`injecao_kwh_ug_${i}` as keyof typeof formData] as number)} onChange={handleNumericInput} onBlur={handleNumericBlur} name={`injecao_kwh_ug_${i}`} disabled={!isEditing} />
                                                        </div>
                                                    ))}
                                                </div>
                                                <div
                                                    ref={saldosRef}
                                                    className={`flex-1 flex flex-col justify-between border-l border-gray-200 dark:border-gray-700 pl-4 space-y-2 rounded-sm transition-shadow ${destacarSaldos && !saldosConfirmados ? 'ring-2 ring-amber-400 ring-offset-4 dark:ring-offset-gray-800' : ''}`}
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-bold">Crédito recebido:</span>
                                                        <input type="text" inputMode="decimal" className="w-24 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1 font-medium text-right" value={numVal('credito_recebido', formData.credito_recebido)} onChange={handleNumericInput} onBlur={handleNumericBlur} name="credito_recebido" disabled={!isEditing} />
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-bold">Saldo do mês:</span>
                                                        <input type="text" inputMode="decimal" className="w-24 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1 font-medium text-right" value={numVal('saldo_acumulado_kwh', formData.saldo_acumulado_kwh)} onChange={handleNumericInput} onBlur={handleNumericBlur} name="saldo_acumulado_kwh" disabled={!isEditing} />
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-bold">Saldo Acumulado Vigor:</span>
                                                        <input type="text" inputMode="decimal" className="w-24 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1 font-medium text-right" value={numVal('saldo_ugs_vigor', formData.saldo_ugs_vigor)} onChange={handleNumericInput} onBlur={handleNumericBlur} name="saldo_ugs_vigor" disabled={!isEditing} />
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-bold">Saldo Acumulado Outros:</span>
                                                        <input type="text" inputMode="decimal" className="w-24 bg-transparent border-b border-gray-300 dark:border-gray-600 focus:outline-none focus:border-primary px-1 font-medium text-right" value={numVal('saldo_ugs_outros', formData.saldo_ugs_outros)} onChange={handleNumericInput} onBlur={handleNumericBlur} name="saldo_ugs_outros" disabled={!isEditing} />
                                                    </div>
                                                    <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                                                        {saldosConfirmados ? (
                                                            <div className="flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-bold">
                                                                <CheckCircle size={14} /> Saldos conferidos
                                                            </div>
                                                        ) : (
                                                            <>
                                                                {saldosTodosZerados && (
                                                                    <p className="flex items-start gap-1 mb-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                                                                        <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                                                                        Todos os saldos estão zerados. Confira no PDF se a fatura realmente não traz saldo.
                                                                    </p>
                                                                )}
                                                                <button
                                                                    type="button"
                                                                    onClick={confirmarSaldos}
                                                                    disabled={isEditing}
                                                                    className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-[#5F6C37] bg-[#5F6C37]/10 text-[#5F6C37] dark:text-[#b5c28a] dark:border-[#b5c28a] font-bold hover:bg-[#5F6C37]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <CheckCircle size={14} /> {isEditing ? 'Conclua a edição para confirmar' : 'Confirmar saldos'}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">

                                    {/* Input Group 1 - Variáveis de Consumo */}
                                    <div className="space-y-4">
                                        <h4 className="text-xs font-black uppercase tracking-widest text-[#5F6C37] mb-2">Variáveis de Consumo</h4>
                                        <InputGroup
                                            label="Consumo Ativo"
                                            name="consumo_ativo_kwh"
                                            value={formData.consumo_ativo_kwh}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            icon={Zap}
                                            colorClass="bg-blue-50/80 dark:bg-blue-900/10 border-blue-100/50"
                                            iconColor="text-blue-500"
                                        />
                                        <InputGroup
                                            label="Energia Injetada GD1"
                                            name="energia_injetada_gd1_kwh"
                                            value={formData.energia_injetada_gd1_kwh}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            icon={Sun}
                                            colorClass="bg-blue-50/80 dark:bg-blue-900/10 border-blue-100/50"
                                            iconColor="text-blue-500"
                                        />
                                        <InputGroup
                                            label="Energia Injetada GD2"
                                            name="energia_injetada_gd2_kwh"
                                            value={formData.energia_injetada_gd2_kwh}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            icon={Sun}
                                            colorClass="bg-blue-50/80 dark:bg-blue-900/10 border-blue-100/50"
                                            iconColor="text-blue-500"
                                        />
                                    </div>

                                    {/* Input Group 2 - Tarifas (Divided in 2 Columns) */}
                                    <div className="space-y-4 md:col-span-2">
                                        <h4 className="text-xs font-black uppercase tracking-widest text-[#5F6C37] mb-2">Tarifas (R$)</h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <InputGroup
                                                label="Tarifa Distribuidora"
                                                name="valor_tarifa_distribuidora"
                                                value={formData.valor_tarifa_distribuidora}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                icon={Tag}
                                                colorClass="bg-green-50/80 dark:bg-green-900/10 border-green-100/50"
                                                iconColor="text-green-600"
                                                step="0.000001"
                                            />
                                            <InputGroup
                                                label="Tarifa Bandeira"
                                                name="tarifa_bandeira"
                                                value={formData.tarifa_bandeira}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                icon={Tag}
                                                colorClass="bg-green-50/80 dark:bg-green-900/10 border-green-100/50"
                                                iconColor="text-green-600"
                                                step="0.000001"
                                            />
                                            <InputGroup
                                                label="Tarifa Injeção GD1"
                                                name="tarifa_injecao_gd1"
                                                value={formData.tarifa_injecao_gd1}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                icon={Tag}
                                                colorClass="bg-green-50/80 dark:bg-green-900/10 border-green-100/50"
                                                iconColor="text-green-600"
                                                step="0.000001"
                                            />
                                            <InputGroup
                                                label="Tarifa Injeção GD2"
                                                name="tarifa_injecao_gd2"
                                                value={formData.tarifa_injecao_gd2}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                icon={Tag}
                                                colorClass="bg-green-50/80 dark:bg-green-900/10 border-green-100/50"
                                                iconColor="text-green-600"
                                                step="0.000001"
                                            />
                                            <InputGroup
                                                label="Tarifa Parc. Injet. GD2"
                                                name="tarifa_parc_injet_s_desc_gd2"
                                                value={formData.tarifa_parc_injet_s_desc_gd2}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                icon={Tag}
                                                colorClass="bg-green-50/80 dark:bg-green-900/10 border-green-100/50"
                                                iconColor="text-green-600"
                                                step="0.000001"
                                            />
                                            <InputGroup
                                                label="Tarifa SCEE c/ Imp"
                                                name="consumo_scee_tarifa_c_imposto"
                                                value={formData.consumo_scee_tarifa_c_imposto}
                                                onChange={handleInputChange}
                                                disabled={!isEditing}
                                                icon={Tag}
                                                colorClass="bg-green-50/80 dark:bg-green-900/10 border-green-100/50"
                                                iconColor="text-green-600"
                                                step="0.000001"
                                            />
                                        </div>
                                    </div>

                                    {/* Input Group 3 - Outros Valores */}
                                    <div className="space-y-4">
                                        <h4 className="text-xs font-black uppercase tracking-widest text-[#5F6C37] mb-2">Outros Valores (R$)</h4>
                                        <InputGroup
                                            label="Energia Não Isenta"
                                            name="energia_nao_isenta"
                                            value={formData.energia_nao_isenta}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            icon={Coins}
                                            colorClass="bg-orange-50/80 dark:bg-orange-900/10 border-orange-100/50"
                                            iconColor="text-orange-600"
                                        />
                                        <InputGroup
                                            label="Ilum. Pública"
                                            name="ilum_publica"
                                            value={formData.ilum_publica}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            icon={Lightbulb}
                                            colorClass="bg-orange-50/80 dark:bg-orange-900/10 border-orange-100/50"
                                            iconColor="text-orange-600"
                                        />
                                        <InputGroup
                                            label="Outros / Multas"
                                            name="outros_multas"
                                            value={formData.outros_multas}
                                            onChange={handleInputChange}
                                            disabled={!isEditing}
                                            icon={AlertTriangle}
                                            colorClass="bg-orange-50/80 dark:bg-orange-900/10 border-orange-100/50"
                                            iconColor="text-orange-600"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer - Final Result & Actions */}
                        <div className="p-8 bg-gray-50 dark:bg-gray-800/80 border-t border-gray-100 dark:border-gray-700 flex flex-col md:flex-row justify-between items-center gap-6 shrink-0 z-10 backdrop-blur-md">

                            <div
                                ref={totalRef}
                                className={`flex flex-col items-center md:items-start rounded-xl transition-shadow ${destacarDiferenca && !diferencaOk ? 'ring-2 ring-amber-400 ring-offset-4 ring-offset-gray-50 dark:ring-offset-gray-800' : ''}`}
                            >
                                {conferencia.codigo !== null ? (
                                    <>
                                        <p className="text-sm font-bold text-[#5F6C37] mb-1">Conta da Equatorial (código de barras)</p>
                                        <div className="text-5xl font-black text-gray-900 dark:text-white tracking-tight">
                                            R$ {conferencia.codigo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        {conferencia.diverge ? (
                                            <div className="mt-2 max-w-md rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                                                <p className="flex items-start gap-1.5 font-bold">
                                                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                                    A soma das parcelas dá R$ {conferencia.soma.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}: R$ {Math.abs(conferencia.diferenca).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {conferencia.diferenca > 0 ? 'a mais' : 'a menos'} que a conta.
                                                </p>
                                                <p className="mt-1">
                                                    O boleto usa o valor do código de barras. A diferença indica parcela lida errada: confira as tarifas com o PDF, porque uma tarifa errada também muda a parte da Vigor.
                                                </p>
                                                {diferencaOk ? (
                                                    <div className="mt-2 flex items-center gap-1.5 font-bold text-emerald-700 dark:text-emerald-400">
                                                        <CheckCircle size={14} /> Tarifas conferidas
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={confirmarDiferenca}
                                                        disabled={isEditing}
                                                        className="mt-2 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500 bg-white text-amber-800 font-bold hover:bg-amber-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed dark:bg-transparent dark:text-amber-300 dark:hover:bg-amber-900/40"
                                                    >
                                                        <CheckCircle size={14} /> {isEditing ? 'Conclua a edição para confirmar' : 'Conferi as tarifas'}
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-[10px] text-gray-400 mt-1 uppercase font-bold tracking-widest">
                                                Soma das parcelas: R$ {conferencia.soma.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — confere
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p className="text-sm font-bold text-[#5F6C37] mb-1">Este é o valor total dessa fatura?</p>
                                        <div className="text-5xl font-black text-gray-900 dark:text-white tracking-tight">
                                            R$ {calculated.final_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        <p className="text-[10px] text-gray-400 mt-1 uppercase font-bold tracking-widest">Sem código de barras: vale a soma das parcelas.</p>
                                    </>
                                )}
                                {!saldosConfirmados && (
                                    <p className="flex items-center gap-1 mt-2 text-xs font-bold text-amber-600 dark:text-amber-400">
                                        <AlertTriangle size={12} /> Confirme os saldos (em Mensagens Importantes) antes de validar.
                                    </p>
                                )}
                            </div>

                            <div className="flex items-center gap-3 w-full md:w-auto">
                                {!isEditing ? (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setSkipConfirmation(true)}
                                            className="bg-secondary/10 border border-secondary text-secondary-dark px-3 py-2 rounded-lg font-black hover:bg-secondary/20 transition-all flex items-center gap-2 shadow-sm"
                                        >
                                            <Calculator size={20} /> Não precisa calcular?
                                        </button>
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-white px-3 py-2 rounded-lg font-black hover:bg-gray-50 dark:hover:bg-gray-600 transition-all flex items-center gap-2 shadow-sm"
                                        >
                                            <Calculator size={20} /> Corrigir dados
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={handleStopEditing}
                                        className="bg-[#5F6C37]/10 border border-[#5F6C37] text-[#5F6C37] px-5 py-3.5 rounded-2xl font-black hover:bg-[#5F6C37]/20 transition-all flex items-center gap-2"
                                    >
                                        <Save size={20} /> Concluir edição
                                    </button>
                                )}

                                <button
                                    onClick={() => setDeleteConfirmation(true)}
                                    className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 text-red-600 px-3 py-2 rounded-lg font-black hover:bg-red-100 dark:hover:bg-red-900/20 transition-all flex items-center gap-2"
                                >
                                    <Trash2 size={20} /> Excluir
                                </button>

                                <button
                                    onClick={handleValidate}
                                    disabled={loading || isEditing}
                                    title={!saldosConfirmados ? 'Confirme os saldos antes de validar' : !diferencaOk ? 'Confira as tarifas antes de validar' : undefined}
                                    className={`bg-[#5F6C37] text-white px-8 py-3.5 rounded-2xl font-black shadow-xl shadow-[#5F6C37]/30 hover:shadow-[#5F6C37]/40 hover:-translate-y-1 active:translate-y-0 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${podeValidar ? '' : 'opacity-60'}`}
                                >
                                    <CheckCircle size={20} /> Validar Fatura
                                </button>
                            </div>
                        </div>

                    </div>
                )}
            </div>,
            document.body
        )
    );
};

const InputGroup = ({ label, name, value, onChange, disabled, icon: Icon, colorClass, iconColor, step = "0.01", type = "number" }: any) => (
    <div className={`p-3 rounded-2xl border transition-all ${colorClass} ${disabled ? 'opacity-90' : 'hover:border-primary/30'}`}>
        <div className="flex items-center gap-2 mb-2 px-1">
            <div className={`p-1.5 rounded-lg bg-white dark:bg-gray-800 shadow-sm ${iconColor}`}>
                {Icon && <Icon size={14} />}
            </div>
            <label className="text-[10px] whitespace-nowrap font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider">{label}</label>
        </div>
        <input
            type={type}
            step={step}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            className={`w-full bg-white dark:bg-gray-800 border-none rounded-xl py-2 px-3 text-lg font-black text-gray-900 dark:text-white outline-none transition-all shadow-sm focus:ring-2 focus:ring-[#5F6C37]/20 ${disabled ? 'cursor-not-allowed' : ''}`}
        />
    </div>
);

export default FaturaValidationModal;
